import { useEffect, useState } from "react";
import { api } from "../../services/api";

export interface GoogleEvent {
  id: string; title: string; start: string; end: string;
  allDay: boolean; location: string; url: string;
  reservationId?: string;
}
interface Status { enabled: boolean; connected: boolean; calendarGranted: boolean }
export function eventOnDate(event: GoogleEvent, date: Date) {
  if (event.allDay) {
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    return event.start <= key && event.end > key;
  }
  const end = new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1);
  return Date.parse(event.start) < end.getTime() && Date.parse(event.end) > date.getTime();
}
export default function useGoogleCalendar(month: Date, reservationRevision?: unknown) {
  const [status, setStatus] = useState<Status>();
  const [items, setItems] = useState<GoogleEvent[]>([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [revision, setRevision] = useState(0);
  const [disconnecting, setDisconnecting] = useState(false);
  const year = month.getFullYear(), monthIndex = month.getMonth();
  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    setItems([]); setError(""); setLoading(true);
    async function load() {
      const next = await api<Status>("/auth/google/status", { signal });
      if (signal.aborted) return;
      setStatus(next);
      if (!next.enabled || !next.calendarGranted) return;
      const first = new Date(year, monthIndex, 1);
      const offset = (first.getDay() + 6) % 7;
      const days = new Date(year, monthIndex + 1, 0).getDate();
      const start = new Date(year, monthIndex, 1 - offset);
      const end = new Date(year, monthIndex, 1 - offset + Math.ceil((offset + days) / 7) * 7);
      const params = new URLSearchParams({ start: start.toISOString(), end: end.toISOString() });
      const result = await api<{ items: GoogleEvent[] }>("/auth/google/events?" + params, { signal });
      if (!signal.aborted) setItems(result.items);
    }
    load().catch((cause: Error) => { if (!signal.aborted) setError(cause.message); })
      .finally(() => { if (!signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [year, monthIndex, revision, reservationRevision]);
  useEffect(() => {
    if (!status?.calendarGranted) return;
    const interval = window.setInterval(() => setRevision(value => value + 1), 5 * 60000);
    return () => window.clearInterval(interval);
  }, [status?.calendarGranted]);
  async function disconnect() {
    setDisconnecting(true);
    try {
      await api("/auth/google/connection", { method: "DELETE" });
      setItems([]);
      setRevision(value => value + 1);
    } catch (cause) { setError((cause as Error).message); }
    finally { setDisconnecting(false); }
  }
  return { status, items, error, loading, disconnecting, disconnect, refresh: () => setRevision(value => value + 1) };
}
